import { Fragment, type ReactNode, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { type Block, type InlineNode, parseMarkdown } from '@/lib/markdown';

/**
 * Rendu markdown d'un texte libre (description de scénario) en éléments React.
 * S'appuie sur le parser pur `@/lib/markdown` — jamais de HTML brut : le texte
 * est échappé par React, ce qui neutralise tout contenu injecté via un lien de
 * partage. Le style est aligné sur la charte de l'app (pas de plugin typography).
 */

function renderInline(nodes: InlineNode[]): ReactNode {
  return nodes.map((node, i) => {
    switch (node.type) {
      case 'text':
        return <Fragment key={i}>{node.value}</Fragment>;
      case 'strong':
        return (
          <strong key={i} className="font-semibold text-foreground">
            {renderInline(node.children)}
          </strong>
        );
      case 'em':
        return (
          <em key={i} className="italic">
            {renderInline(node.children)}
          </em>
        );
      case 'code':
        return (
          <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
            {node.value}
          </code>
        );
      case 'link':
        return (
          <a
            key={i}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            className="font-medium text-primary underline underline-offset-2"
          >
            {renderInline(node.children)}
          </a>
        );
    }
  });
}

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.type) {
    case 'heading': {
      const cls =
        block.level <= 2
          ? 'mt-4 mb-1 text-base font-semibold text-foreground first:mt-0'
          : 'mt-3 mb-1 text-sm font-semibold text-foreground first:mt-0';
      return (
        <h3 key={key} className={cls}>
          {renderInline(block.children)}
        </h3>
      );
    }
    case 'paragraph':
      return (
        <p key={key} className="my-2 leading-relaxed first:mt-0 last:mb-0">
          {renderInline(block.children)}
        </p>
      );
    case 'ul':
      return (
        <ul key={key} className="my-2 ml-5 list-disc space-y-1">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} className="my-2 ml-5 list-decimal space-y-1">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );
    case 'blockquote':
      return (
        <blockquote key={key} className="my-2 border-l-2 pl-3 italic">
          {renderInline(block.children)}
        </blockquote>
      );
    case 'hr':
      return <hr key={key} className="my-3 border-border" />;
  }
}

export function Markdown({ children, className }: { children: string; className?: string }) {
  const blocks = useMemo(() => parseMarkdown(children), [children]);
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}
